import React, {useState, useEffect, useRef, useMemo} from "react"
import {useQuery} from "react-query"
import useAuth from "auth/useAuth"
import RescuesChart from "./RescuesChart"
import Spinner from "components/Spinner"

const NUM_SEASONS = 5
const INITIAL_POSITION = {lat: 43.295, lng: 20.82}
const BODY_IMG_WIDTH = 233
const BODY_IMG_HEIGHT = 500
const BODY_FRONT_URL = 'https://public.gss.rs/body/body.front.png'
const BODY_BACK_URL = 'https://public.gss.rs/body/body.back.png'

function BodyCanvas({imageUrl, injuries, alpha}) {
    const canvasRef = useRef()
    const imageRef = useRef()

    useEffect(() => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
            imageRef.current = img
            draw()
        }
        img.src = imageUrl
    }, [imageUrl])

    useEffect(() => {
        if (imageRef.current) draw()
    }, [alpha, injuries])

    function draw() {
        const canvas = canvasRef.current
        const img = imageRef.current
        if (!canvas || !img) return
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        for (const injury of injuries) {
            ctx.beginPath()
            ctx.arc(injury.x * canvas.width, injury.y * canvas.height, 8, 0, 2 * Math.PI)
            ctx.fillStyle = `rgba(255, 0, 0, ${alpha})`
            ctx.fill()
        }
    }

    return <canvas ref={canvasRef} width={BODY_IMG_WIDTH} height={BODY_IMG_HEIGHT}/>
}

export default function RescuesTab() {

    const {auth, API} = useAuth()
    const {data, isLoading} = useQuery(
        ["time_distribution"],
        () => auth(API.analyticsTypeGet, {type: 'time_distribution', season: ''}).then(response => response.data)
    )
    const {data: injuryData, isLoading: injuryLoading} = useQuery(
        ["injury_locations"],
        () => auth(API.analyticsTypeGet, {type: 'injury_locations', season: ''}).then(response => response.data)
    )

    const mapRef = useRef()
    const circlesRef = useRef([])
    const [mapsLoaded, setMapsLoaded] = useState(typeof window.google?.maps?.Map === 'function')
    const [alpha, setAlpha] = useState(0.05)
    const [bodyAlpha, setBodyAlpha] = useState(0.05)

    useEffect(() => {
        if (mapsLoaded) return
        const interval = setInterval(() => {
            if (typeof window.google?.maps?.Map === 'function') {
                setMapsLoaded(true)
                clearInterval(interval)
            }
        }, 100)
        return () => clearInterval(interval)
    }, [])

    const map = useMemo(() => {
        if (!mapRef.current || !mapsLoaded) return null

        const gmap = new window.google.maps.Map(mapRef.current, {
            zoom: 13,
            center: INITIAL_POSITION,
            streetViewControl: false,
            mapTypeId: 'terrain',
            mapId: 'gss-analytics-map'
        })

        const arcgis = new window.google.maps.ImageMapType({
            getTileUrl: (coord, zoom) => `https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/${zoom}/${coord.y}/${coord.x}`,
            tileSize: new window.google.maps.Size(256, 256),
            isPng: true,
            maxZoom: 18,
            minZoom: 3,
            name: "ArcGIS"
        })

        const openSnowMap = new window.google.maps.ImageMapType({
            getTileUrl: (coord, zoom) => `https://tiles.opensnowmap.org/pistes/${zoom}/${coord.x}/${coord.y}.png`,
            tileSize: new window.google.maps.Size(256, 256),
            isPng: true,
            maxZoom: 18,
            minZoom: 3,
            name: "OpenSnowMap"
        })

        gmap.overlayMapTypes.push(arcgis)
        gmap.overlayMapTypes.push(openSnowMap)

        return gmap
    }, [mapRef.current, mapsLoaded])

    useEffect(() => {
        if (!map || !data?.locations) return
        circlesRef.current = data.locations.map(loc =>
            new window.google.maps.Circle({
                map,
                center: {lat: loc.lat, lng: loc.lon},
                radius: 40,
                fillColor: 'red',
                fillOpacity: alpha,
                strokeWeight: 0
            })
        )
    }, [map, data])

    useEffect(() => {
        for (const circle of circlesRef.current)
            circle.setOptions({fillOpacity: alpha})
    }, [alpha])

    return <>
        <h1>Vreme povrede</h1>

        <p>
            Raspodela povreda po vremenu u toku dana za poslednjih {NUM_SEASONS} sezona.<br/>
            Svaka kolona predstavlja pola sata.
        </p>

        {isLoading ? <Spinner/> :
            <RescuesChart
                data={data?.time_bins}
                subtitle="po vremenu i skijalištima"
                hAxisOptions={{showTextEvery: 2, slantedText: true, slantedTextAngle: 45}}
            />
        }

        <h1>Geografske lokacije povreda</h1>
        <p>Mapa lokacija povreda za poslednjih {NUM_SEASONS} sezona.</p>

        <div ref={mapRef} style={{height: '65vh', width: '100%'}}/>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px'}}>
            <label>Jačina boje:</label>
            <input type="range" min="0" max="30" step="0.5" value={alpha * 100} onChange={e => setAlpha(parseFloat(e.target.value) / 100)}/>
            <span>{Math.round(alpha * 100)}</span>
        </div>

        <h1>Lokacije povreda na telu</h1>
        <p>Raspodela povreda po lokaciji na telu za poslednjih {NUM_SEASONS} sezona.</p>

        {injuryLoading ? <Spinner/> : injuryData?.injuries && <>
            <div style={{display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap'}}>
                <div style={{textAlign: 'center'}}>
                    <div style={{marginBottom: '4px', fontWeight: 'bold'}}>Napred</div>
                    <BodyCanvas
                        imageUrl={BODY_FRONT_URL}
                        injuries={injuryData.injuries.filter(i => i.image_id === 0)}
                        alpha={bodyAlpha}
                    />
                </div>
                <div style={{textAlign: 'center'}}>
                    <div style={{marginBottom: '4px', fontWeight: 'bold'}}>Nazad</div>
                    <BodyCanvas
                        imageUrl={BODY_BACK_URL}
                        injuries={injuryData.injuries.filter(i => i.image_id === 1)}
                        alpha={bodyAlpha}
                    />
                </div>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px'}}>
                <label>Jačina boje:</label>
                <input type="range" min="0" max="50" step="1" value={bodyAlpha * 100} onChange={e => setBodyAlpha(parseFloat(e.target.value) / 100)}/>
                <span>{Math.round(bodyAlpha * 100)}</span>
            </div>
        </>}
    </>
}
